import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/hooks/useToast'
import { useAuth } from '@/hooks/useAuth'
import { shareInFlight } from '@/lib/sharedRequest'
import type { Contact, ContactCategory } from '@/types/contact'
import type { TaskContext } from '@/types/task'
import { scopeForDomain } from '@/lib/scope'

interface DbContact {
  id: string
  user_id: string
  name: string
  phone: string | null
  email: string | null
  notes: string | null
  category: ContactCategory | null
  birthday: string | null
  relationship: string | null
  preferences: string | null
  place_id: string | null
  created_at: string
  updated_at: string
}

function dbContactToContact(dbContact: DbContact): Contact {
  return {
    id: dbContact.id,
    name: dbContact.name,
    phone: dbContact.phone ?? undefined,
    email: dbContact.email ?? undefined,
    notes: dbContact.notes ?? undefined,
    category: dbContact.category ?? undefined,
    birthday: dbContact.birthday ?? undefined,
    relationship: dbContact.relationship ?? undefined,
    preferences: dbContact.preferences ?? undefined,
    placeId: dbContact.place_id ?? undefined,
    createdAt: new Date(dbContact.created_at),
    updatedAt: new Date(dbContact.updated_at),
  }
}

export function useContacts() {
  const { user } = useAuth()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch contacts on mount and when user changes
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing on auth change is valid
      setContacts([])
      setLoading(false)
      return
    }

    async function fetchContacts() {
      if (!user) return

      setLoading(true)
      setError(null)

      // RLS policies handle household sharing - no need to filter by user_id.
      // One trip shared by the instances that ask at once (shareInFlight, sharedRequest.ts).
      const { data, error: fetchError } = await shareInFlight(`contacts:${user.id}`, () => supabase
        .from('contacts')
        .select('*')
        .order('name', { ascending: true }))

      if (fetchError) {
        setError(fetchError.message)
        setLoading(false)
        return
      }

      setContacts((data as DbContact[]).map(dbContactToContact))
      setLoading(false)
    }

    fetchContacts()
  }, [user])

  const addContact = useCallback(async (contact: {
    name: string
    phone?: string
    email?: string
    notes?: string
    category?: ContactCategory
    birthday?: string
    relationship?: string
    preferences?: string
    placeId?: string
    /** The domain the contact belongs to (e.g. the goal it was added from).
     *  When given, the contact carries it and the scope it implies. */
    context?: TaskContext
  }) => {
    if (!user) return null

    // Dedupe by Google Place id: re-picking a place we already saved should
    // reuse that contact rather than create a duplicate.
    if (contact.placeId) {
      const existing = contacts.find((c) => c.placeId === contact.placeId)
      if (existing) return existing
    }

    // Optimistic update
    const tempId = crypto.randomUUID()
    const optimisticContact: Contact = {
      id: tempId,
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      notes: contact.notes,
      category: contact.category,
      birthday: contact.birthday,
      relationship: contact.relationship,
      preferences: contact.preferences,
      placeId: contact.placeId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }
    setContacts((prev) => [...prev, optimisticContact].sort((a, b) => a.name.localeCompare(b.name)))

    const { data, error: insertError } = await supabase
      .from('contacts')
      .insert({
        user_id: user.id,
        name: contact.name,
        phone: contact.phone ?? null,
        email: contact.email ?? null,
        notes: contact.notes ?? null,
        category: contact.category ?? null,
        birthday: contact.birthday ?? null,
        relationship: contact.relationship ?? null,
        preferences: contact.preferences ?? null,
        place_id: contact.placeId ?? null,
        ...(contact.context ? { context: contact.context, scope: scopeForDomain(contact.context, [], null) } : {}),
      })
      .select()
      .single()

    if (insertError) {
      // Rollback on error
      setContacts((prev) => prev.filter((c) => c.id !== tempId))
      setError(insertError.message)
      return null
    }

    // Replace optimistic contact with real one
    const realContact = dbContactToContact(data as DbContact)
    setContacts((prev) =>
      prev.map((c) => (c.id === tempId ? realContact : c)).sort((a, b) => a.name.localeCompare(b.name))
    )

    return realContact
  }, [user, contacts])

  const updateContact = useCallback(async (id: string, updates: Partial<Contact>) => {
    const contact = contacts.find((c) => c.id === id)
    if (!contact) return

    // Optimistic update
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c)).sort((a, b) => a.name.localeCompare(b.name))
    )

    // Convert Contact updates to DB format
    const dbUpdates: Record<string, unknown> = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone ?? null
    if (updates.email !== undefined) dbUpdates.email = updates.email ?? null
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes ?? null
    if (updates.category !== undefined) dbUpdates.category = updates.category ?? null
    if (updates.birthday !== undefined) dbUpdates.birthday = updates.birthday ?? null
    if (updates.relationship !== undefined) dbUpdates.relationship = updates.relationship ?? null
    if (updates.preferences !== undefined) dbUpdates.preferences = updates.preferences ?? null

    const { error: updateError } = await supabase
      .from('contacts')
      .update(dbUpdates)
      .eq('id', id)

    if (updateError) {
      // Rollback on error
      setContacts((prev) =>
        prev.map((c) => (c.id === id ? contact : c))
      )
      setError(updateError.message)
      showToast("Couldn't save that change to the contact. It was undone.", 'error')
    }
  }, [contacts])

  // Not optimistic: removing the row first sent the open contact page to the
  // list (the contact had vanished) even when the delete then failed. The row
  // leaves only once the delete is confirmed; true = deleted.
  const deleteContact = useCallback(async (id: string): Promise<boolean> => {
    const contactToDelete = contacts.find((c) => c.id === id)
    if (!contactToDelete) return false

    // Confirm the row went: RLS turns a forbidden DELETE into zero rows with
    // no error, so "no error" alone is not "deleted".
    const { data: gone, error: deleteError } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id)
      .select('id')

    if (deleteError || !((gone ?? []) as { id: string }[]).some((c) => c.id === id)) {
      if (deleteError) setError(deleteError.message)
      showToast(deleteError
        ? `Couldn't delete ${contactToDelete.name}.`
        : `Couldn't delete ${contactToDelete.name} — you may not have permission.`, 'error')
      return false
    }
    setContacts((prev) => prev.filter((c) => c.id !== id))
    return true
  }, [contacts])

  // Search contacts by name (case-insensitive)
  const searchContacts = useCallback((query: string): Contact[] => {
    if (!query.trim()) return contacts
    const lowerQuery = query.toLowerCase()
    return contacts.filter((c) => c.name.toLowerCase().includes(lowerQuery))
  }, [contacts])

  // Get contact by ID
  const getContactById = useCallback((id: string): Contact | undefined => {
    return contacts.find((c) => c.id === id)
  }, [contacts])

  // Create a contacts map for efficient lookup
  const contactsMap = useMemo(() => {
    const map = new Map<string, Contact>()
    for (const contact of contacts) {
      map.set(contact.id, contact)
    }
    return map
  }, [contacts])

  return {
    contacts,
    contactsMap,
    loading,
    error,
    addContact,
    updateContact,
    deleteContact,
    searchContacts,
    getContactById,
  }
}
